import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './SideNav.css';
import { mapElementPositions } from '../utils/elementMapper';
import supabase from '../utils/supabaseClient';

const isDevelopment = process.env.NODE_ENV === 'development';

function buildNavTree(isLoggedIn) {
  return [
    {
      id: 'practice',
      label: 'Practice',
      defaultOpen: true,
      children: [
        { id: 'chantersync', label: 'Manage Chanters', to: '/chantersync' },
        { id: 'chanter-test', label: 'Chanter Test', to: '/chanter-test' },
        {
          id: 'games',
          label: 'Games',
          defaultOpen: true,
          children: [
            { id: 'games-hub', label: 'All games', to: '/games' },
            { id: 'blaster', label: 'Note Blaster', to: '/blaster' },
            { id: 'note-battle', label: 'Note Battle', to: '/note-battle' },
            { id: 'simon', label: 'Melodic Simon', to: '/simon' },
            { id: 'echo', label: 'Phrase Echo', to: '/echo' },
          ],
        },
      ],
    },
    {
      id: 'learn',
      label: 'Learn',
      defaultOpen: true,
      children: [
        {
          id: 'recommended-teachers',
          label: 'Recommended teachers',
          href: '/learn/recommended-teachers.html',
        },
        { id: 'flashcards', label: 'Flash Cards', to: '/flashcards' },
        { id: 'basic-notes', label: 'Basic Notes', to: '/basic-notes' },
        { id: 'gracenotes', label: 'Gracenotes', to: '/gracenotes' },
        { id: 'chanter-render', label: 'Chanter View', to: '/chanter-render' },
      ],
    },
    {
      id: 'tools',
      label: 'Tools',
      defaultOpen: false,
      children: [
        { id: 'tuner', label: 'Tuner', to: '/tuner' },
        { id: 'metronome', label: 'Metronome', to: '/metronome' },
      ],
    },
    {
      id: 'library',
      label: 'Library',
      defaultOpen: false,
      children: [
        { id: 'sheet-share', label: 'Sheet library', to: '/sheet-share' },
        { id: 'playlists', label: 'Playlists', to: '/playlists' },
        { id: 'aisheetnovel', label: 'AI Composer', to: '/aisheetnovel' },
        { id: 'import-song', label: 'Import', to: '/import-song' },
      ],
    },
    {
      id: 'community',
      label: 'Community',
      defaultOpen: false,
      children: [
        { id: 'articles', label: 'Articles', href: '/learn/' },
        { id: 'instruction', label: 'Find instructors', to: '/instruction' },
        { id: 'bands', label: 'Find bands', to: '/bands' },
        { id: 'resources', label: 'Resources', to: '/resources' },
      ],
    },
    {
      id: 'support',
      label: 'Support',
      defaultOpen: false,
      children: [
        { id: 'faq', label: 'Help & FAQ', href: '/faq/' },
        { id: 'microphone-help', label: 'Microphone Help', to: '/microphone-help' },
        { id: 'about-us', label: 'About', to: '/about-us' },
        { id: 'contact', label: 'Contact', to: '/contact' },
        { id: 'feature-request', label: 'Feature Request', to: '/feature-request' },
      ],
    },
    {
      id: 'account',
      label: 'Account',
      defaultOpen: false,
      children: [
        isLoggedIn
          ? { id: 'user-profile', label: 'My Profile', to: '/user-profile' }
          : { id: 'login', label: 'Sign up / Log in', to: '/auth' },
        { id: 'settings', label: 'Settings', to: '/settings' },
        ...(isLoggedIn ? [{ id: 'logout', label: 'Log out', action: 'logout' }] : []),
      ],
    },
    ...(isDevelopment
      ? [
          {
            id: 'dev',
            label: 'Dev',
            defaultOpen: false,
            children: [
              { id: 'omr', label: 'OMR Scanner', to: '/omr' },
              { id: 'render-test', label: 'Test', to: '/render-test' },
              { id: 'animate-song', label: 'Animate Song', to: '/animate-song' },
              { id: 'play-along', label: 'Play Along', to: '/play-along' },
              { id: 'fire-demo', label: 'Fire Effect', to: '/fire-demo' },
              { id: 'template1', label: 'Template1', to: '/template1' },
              { id: 'template2', label: 'Template2', to: '/template2' },
              { id: 'database-test', label: 'Database Test', to: '/database-test' },
              { id: 'blaster-high-scores', label: 'Blaster High Scores', to: '/blaster-high-scores' },
              { id: 'ai-test', label: 'AI Test', to: '/ai-test' },
              { id: 'pitch-testbed', label: 'Pitch Testbed', to: '/dev/pitch-testbed' },
              { id: 'admin-songs', label: 'Admin Songs', to: '/admin-songs' },
              { id: 'map-elements', label: 'Map Elements', action: 'map-elements' },
            ],
          },
        ]
      : []),
  ];
}

function pathMatches(pathname, to) {
  if (!to) return false;
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

function collectOpenSectionIds(nodes, pathname, open = new Set()) {
  nodes.forEach(node => {
    if (node.children?.length) {
      const childActive = node.children.some(
        child =>
          pathMatches(pathname, child.to) ||
          (child.children &&
            child.children.some(grand => pathMatches(pathname, grand.to)))
      );
      if (childActive || node.defaultOpen) {
        open.add(node.id);
      }
      collectOpenSectionIds(node.children, pathname, open);
    }
  });
  return open;
}

function SideNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openSections, setOpenSections] = useState(() => new Set());

  const navTree = useMemo(() => buildNavTree(isLoggedIn), [isLoggedIn]);

  useEffect(() => {
    let mounted = true;
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (mounted) setIsLoggedIn(!!data.session);
    };
    checkSession();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setIsLoggedIn(!!session);
    });
    return () => {
      mounted = false;
      listener?.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setOpenSections(collectOpenSectionIds(navTree, location.pathname));
    setDrawerOpen(false);
  }, [location.pathname, navTree]);

  useEffect(() => {
    document.body.classList.toggle('side-nav-drawer-open', drawerOpen);
    return () => document.body.classList.remove('side-nav-drawer-open');
  }, [drawerOpen]);

  const toggleSection = id => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const closeDrawer = () => setDrawerOpen(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    closeDrawer();
    navigate('/auth');
  };

  const handleAction = action => {
    if (action === 'logout') {
      handleLogout();
      return;
    }
    if (action === 'map-elements') {
      mapElementPositions();
      closeDrawer();
    }
  };

  const renderItems = (nodes, depth = 0) => (
    <ul className={`side-nav__list side-nav__list--depth-${depth}`}>
      {nodes.map(node => {
        if (node.children?.length) {
          const isOpen = openSections.has(node.id);
          return (
            <li key={node.id} className="side-nav__section">
              <button
                type="button"
                className={`side-nav__section-toggle${isOpen ? ' is-open' : ''}`}
                aria-expanded={isOpen}
                onClick={() => toggleSection(node.id)}
              >
                <span>{node.label}</span>
                <span className="side-nav__chevron" aria-hidden="true">
                  {isOpen ? '▾' : '▸'}
                </span>
              </button>
              {isOpen && renderItems(node.children, depth + 1)}
            </li>
          );
        }

        if (node.href) {
          return (
            <li key={node.id}>
              <a href={node.href} className="side-nav__link" onClick={closeDrawer}>
                {node.label}
              </a>
            </li>
          );
        }

        if (node.action) {
          return (
            <li key={node.id}>
              <button
                type="button"
                className="side-nav__link side-nav__link--button"
                onClick={() => handleAction(node.action)}
              >
                {node.label}
              </button>
            </li>
          );
        }

        return (
          <li key={node.id}>
            <NavLink
              to={node.to}
              className={({ isActive }) =>
                `side-nav__link${isActive ? ' is-active' : ''}`
              }
              end={node.to === '/'}
              onClick={closeDrawer}
            >
              {node.label}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <header className="side-nav__mobile-bar">
        <button
          type="button"
          className={`side-nav__menu-btn${drawerOpen ? ' is-open' : ''}`}
          aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(open => !open)}
        >
          <span />
          <span />
          <span />
        </button>
        <Link to="/" className="side-nav__mobile-brand" onClick={closeDrawer}>
          BagpipeBlasphemy
        </Link>
        <Link to="/chantersync" className="side-nav__mobile-cta" onClick={closeDrawer}>
          Practice
        </Link>
      </header>

      {drawerOpen && (
        <button
          type="button"
          className="side-nav__backdrop"
          aria-label="Close menu"
          onClick={closeDrawer}
        />
      )}

      <aside className={`side-nav${drawerOpen ? ' is-open' : ''}`} aria-label="Main">
        <div className="side-nav__brand-block">
          <Link to="/" className="side-nav__brand" onClick={closeDrawer}>
            BagpipeBlasphemy
          </Link>
          <p className="side-nav__tagline">Digital chanter practice</p>
        </div>

        <div className="side-nav__scroll">
          <div className="side-nav__primary">
            <NavLink
              to="/chantersync"
              className={({ isActive }) =>
                `side-nav__cta${isActive ? ' is-active' : ''}`
              }
              onClick={closeDrawer}
            >
              Get started
            </NavLink>
          </div>
          {renderItems(navTree)}
        </div>
      </aside>
    </>
  );
}

export default SideNav;
